<template>
  <div class="column q-pa-md ">
    <div class="col-1 row q-gutter-md items-center">
      <div class="column">
        <h5 class="q-ma-xs">{{ task?.name }}</h5>
        <div class="q-ma-xs text-subtitle2">{{ task?.code }}</div>
      </div>
    </div>

    <div class="col-10 row ">
      <div class="col-12 col-md-6">
        <q-expansion-item switch-toggle-side expand-separator label="Детали">
          <div class="row  q-ma-md  items-center q-gutter-md">
            <div class="col-4">
              Количество фото:
              <span>{{ task?.photoCount }}</span>
            </div>
            <div class="col-4">
              Тип:
              <span>{{ task?.type.name }}</span>
            </div>
            <div class="col-4">
              <span>Статус: </span>
              <q-chip :color="statusColors[task?.status]" text-color="white">{{ status[task?.status] }}</q-chip>
            </div>
            <div class="col-4">
              <span>Состояние: </span>
              <q-chip :color="stateColors[task?.state]" text-color="white">{{ states[task?.state] }}</q-chip>
            </div>
            <div class="col-4">
              <span>Ярлыки: </span>
              <q-chip :style="{ backgroundColor: label.color }" text-color="white" v-for="label in task?.labels">
                {{ label.name }}
              </q-chip>
            </div>
            <div class="col-4">
              Создан:
              <span>{{ dayJs.utc(task?.createdAt).local().fromNow() }}</span>
            </div>
            <div class="col-">
              <span>Обновлен: </span>
              <span>{{ dayJs.utc(task?.updatedAt).local().fromNow() }}</span>
            </div>
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12 col-md-6">
        <q-expansion-item switch-toggle-side expand-separator label="Пользователи">
          <div class="row q-ma-md items-center">
            <div class="col">
              Создатель:
              <span>{{ task?.owner.username }}</span>
            </div>
            <div class="col">
              <span>Исполнители: </span>
              <span>{{ task?.assigned.map(u => u.username).join(", ") }}</span>
            </div>
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12" v-if="task?.properties.length > 0">
        <q-expansion-item switch-toggle-side expand-separator label="Пользовательские детали">
          <div class="row q-ma-md items-center">
            <div class="col" v-for="property in task?.properties">
              {{ propertiesDescription[property.code].name }}:
              <span v-if="propertiesDescription[property.code].type === 'DATE'">
                {{ dayJs(property.value).format("L") }}
              </span>
              <span v-else-if="propertiesDescription[property.code].type === 'DATETIME'">
                {{ dayJs(property.value).format("L LT") }}
              </span>
              <span v-else>
                {{ property.value }}
              </span>
            </div>
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12">
        <q-expansion-item switch-toggle-side expand-separator label="Описание">
          <div class="q-ma-md">
            {{ task?.description || "Описания нет" }}
          </div>
        </q-expansion-item>
      </div>
      <div class="col-12">
        <q-expansion-item switch-toggle-side expand-separator :disable="task?.photos.length < 1"
          :caption="task?.photos.length < 1 ? 'Фотографии отсутствуют' : undefined" label="Фотографии">
          <div class="row q-gutter-md q-ma-xs">
            <div class="col-12 col-md-2" v-for="photo in task?.photos.slice(0, 3)">
              <q-card v-if="photo.caption">
                <q-img fit="cover" :ratio="16 / 9" v-ripple
                  :src="photoCaptionUrl(photo.address, photo.caption)" />
              </q-card>
              <q-card v-else>
                <q-img fit="cover" :ratio="16 / 9" v-ripple :src="photoOriginalUrl(photo.address)" />
              </q-card>

            </div>
            <div class="col-12 col-md-2" :style="$q.screen.xs ? ({ minHeight: '150px' }) : {}">
              <q-card v-ripple @click="openPhotos = !openPhotos" class="bg-grey-4 fit text-center ">
                <q-icon name="more_horiz" color="white" class="absolute-center" size="60px" />
              </q-card>
            </div>
          </div>
        </q-expansion-item>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDayJs } from "../../composables/dayjs";
import { useQuasar } from "quasar";
import { useRoute, useRouter } from "vue-router";
import { computed, ref, watch } from "vue";
import { useTask, useCloseTask, useTaskLink, useSharedTask } from "../../composables/task";
import { useProjectStore } from "../../store/project";
import { storeToRefs } from "pinia";
import { status, statusColors, stateColors, states } from "../../util/state";
import Avatar from "../../components/Avatar.vue";
import { usePhotoStore } from "../../store/photo";
import TaskLogs from "../../components/TaskLogs.vue";
import TaskComments from "../../components/TaskComments.vue";
import { useEventBus } from "@vueuse/core";
import { taskKey } from "../../util/events";
import { useRouteQuery } from "@vueuse/router";
import { photoCdnUrl } from "../../util/photoCdn";

const route = useRoute()
const router = useRouter()

const token = useRouteQuery('share')
const $q = useQuasar()

const tab = ref("logs")
const dayJs = useDayJs()


const projectId = computed(() => Number(route.params.projectId))
const taskId = computed(() => Number(route.params.taskId))
const propertiesDescription = computed(() => Object.fromEntries(
  task.value?.type.propertyDescription.map(e => [e.code, e]) ?? []
))

const { data: task, isFetching, error, execute } = useSharedTask(token)

const taskEvent = useEventBus(taskKey)

const photoStore = usePhotoStore()
const { open: openPhotos, photos, shared } = storeToRefs(photoStore)
shared.value = true
watch(task, (val) => {
  photos.value = val?.photos
})

watch(error, val => {
  if (val) {
    router.push('/404')
  }
})

const photoCaptionUrl = (address: string, caption: string) => photoCdnUrl(address, {
  resize: 'x720',
  caption: { text: caption, fontSize: 16 },
  formatAfterCaption: 'jpeg',
})

const photoOriginalUrl = (address: string) => photoCdnUrl(address, { encode: true })
</script>

<style scoped></style>
